import React, { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, ComposedChart, Legend, Cell, FunnelChart, Funnel, LabelList,
} from 'recharts';
import { Zap, AlertTriangle, ChevronRight, Phone, Clock, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dispositionMeta } from '@/lib/dispositions';
import WhatsAppBulkDialog from '@/components/whatsapp/WhatsAppBulkDialog';

const CHART_H = 320;

/* --------------------------- LAPSED PATIENTS --------------------------- */
// Stable key for a params object so effects re-run only when filters change.
const pkey = (p) => JSON.stringify(p || {});

export function LapsedTab({ params: filterParams = {} }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [band, setBand] = useState('90-180');
  const [dispo, setDispo] = useState('');
  const [loading, setLoading] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const params = { ...filterParams, limit: 300, band: opts.band ?? band };
      if (opts.dispo ?? dispo) params.disposition = opts.dispo ?? dispo;
      const r = await api.get('/reports/lapsed-patients', { params });
      setData(r.data);
    } catch { toast.error('Failed to load lapsed patients'); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pkey(filterParams)]);

  const bandColors = { '30-60': '#0284C7', '60-90': '#B45309', '90-180': '#DC2626', '180+': '#7F1D1D' };

  return (
    <div className="space-y-3" data-testid="reports-lapsed-tab">
      <Card className="p-4 border-stone-200">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" /> Re-engagement opportunities
            </div>
            <h3 className="font-heading text-lg font-bold text-stone-900">Lapsed Patients — Age Buckets</h3>
            <p className="text-xs text-stone-500 mt-0.5">Non-terminal patients who haven&apos;t been touched recently. Click a bucket to drill into names.</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total lapsed</div>
            <div className="font-heading text-2xl font-bold text-stone-900 tabular-nums" data-testid="lapsed-total">
              {data?.total_lapsed?.toLocaleString() || '—'}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {(data?.bands || []).map(b => (
            <button
              key={b.band}
              onClick={() => { setBand(b.band); load({ band: b.band }); }}
              data-testid={`lapsed-band-${b.band}`}
              className={`text-left border-2 rounded-lg px-3 py-2.5 transition-all hover:shadow-md ${
                band === b.band ? 'border-stone-900 bg-stone-50' : 'border-stone-200 bg-white'
              }`}
              style={{ borderLeftColor: bandColors[b.band], borderLeftWidth: 4 }}
            >
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                {b.band === '180+' ? '180+ days' : `${b.band} days`}
              </div>
              <div className="font-heading text-xl font-bold text-stone-900 tabular-nums mt-1">
                {b.count.toLocaleString()}
              </div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-3 border-stone-200">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Filter</span>
          <Select value={dispo || 'all'} onValueChange={(v) => { const nv = v === 'all' ? '' : v; setDispo(nv); load({ dispo: nv }); }}>
            <SelectTrigger className="h-8 w-52 text-xs">
              <SelectValue placeholder="All dispositions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All non-terminal statuses</SelectItem>
              {['hot_lead','call_later','call_not_picked','progressing_slow','long_term_pot','three_six_m_potential','on_trial','booked','appt_booked','lead_received'].map(d => (
                <SelectItem key={d} value={d}>{dispositionMeta(d).label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-stone-500 ml-2">
            Showing <b className="text-stone-800">{data?.patients?.length || 0}</b> patients in bucket <b className="text-stone-800">{band}</b>
          </div>
          <div className="flex-1" />
          <Button size="sm" variant="default" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setBulkOpen(true)}
            disabled={!(data?.patients || []).some(p => p.patient_mobile)}
            data-testid="lapsed-bulk-wa">
            <MessageCircle className="w-3 h-3 mr-1" /> Bulk WhatsApp ({(data?.patients || []).filter(p => p.patient_mobile).length})
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => load()} data-testid="lapsed-refresh">
            <Clock className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <div className="overflow-auto max-h-[600px] hc-scrollbar rounded border border-stone-200">
          <table className="w-full text-xs">
            <thead className="bg-stone-100 sticky top-0">
              <tr>
                {['Patient','Mobile','Status','Clinic','Audiologist','Source','Sub-source','Days idle','Last comment'].map(h => (
                  <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-700 border-b border-stone-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.patients || []).map((p) => {
                const m = dispositionMeta(p.disposition);
                return (
                  <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-2 py-1.5 font-medium text-stone-900">{p.patient_name || '—'}</td>
                    <td className="px-2 py-1.5 tabular-nums text-stone-700">
                      {p.patient_mobile ? (
                        <a href={`tel:${p.patient_mobile}`} className="flex items-center gap-1 text-blue-700 hover:underline">
                          <Phone className="w-3 h-3" /> {p.patient_mobile}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-1.5"><span className="text-[10px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: m.bg, color: m.text }}>{m.label}</span></td>
                    <td className="px-2 py-1.5 text-stone-700">{p.clinic_name || '—'}</td>
                    <td className="px-2 py-1.5 text-stone-700">{p.audiologist_name || '—'}</td>
                    <td className="px-2 py-1.5 text-stone-700">{p.source_name || '—'}</td>
                    <td className="px-2 py-1.5 text-stone-500 text-[11px]">{p.subsource_name || '—'}</td>
                    <td className="px-2 py-1.5 tabular-nums font-semibold" style={{ color: p.days_lapsed > 180 ? '#7F1D1D' : p.days_lapsed > 90 ? '#DC2626' : p.days_lapsed > 60 ? '#B45309' : '#0284C7' }}>
                      {p.days_lapsed ?? '—'}d
                    </td>
                    <td className="px-2 py-1.5 text-stone-500 text-[11px] max-w-md truncate">{p.last_comment || '—'}</td>
                  </tr>
                );
              })}
              {(!loading && !(data?.patients || []).length) && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-stone-400 text-xs">No lapsed patients in this bucket.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <WhatsAppBulkDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        recipients={(data?.patients || []).filter(p => p.patient_mobile).map(p => ({
          to: p.patient_mobile, patient_name: p.patient_name, ticket_id: p.id,
        }))}
        defaultTemplateId="lapsed_reengage"
      />
    </div>
  );
}

/* --------------------------- COHORT RETENTION --------------------------- */
export function CohortTab({ params = {} }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/reports/cohort-retention', { params: { ...params, months: 18 } })
      .then(r => setData(r.data)).catch(() => toast.error('Failed to load cohorts'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkey(params)]);

  const rows = data?.data || [];
  const maxAcq = Math.max(...rows.map(r => r.acquired), 1);
  const maxConv = Math.max(...rows.map(r => r.conversion_pct), 1);

  return (
    <div className="space-y-3" data-testid="reports-cohort-tab">
      <Card className="p-4 border-stone-200">
        <h3 className="font-heading text-lg font-bold text-stone-900">Cohort Retention — 18 Months</h3>
        <p className="text-xs text-stone-500 mt-0.5">Monthly patient acquisition vs eventual conversion. Heatmap intensity = conversion strength.</p>
      </Card>

      <Card className="p-3 border-stone-200 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-stone-100">
            <tr>
              <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Cohort (Lead Month)</th>
              <th className="px-2 py-1.5 text-right font-semibold text-stone-700">Acquired</th>
              <th className="px-2 py-1.5 text-right font-semibold text-stone-700">Booked</th>
              <th className="px-2 py-1.5 text-right font-semibold text-stone-700">Sold</th>
              <th className="px-2 py-1.5 text-right font-semibold text-stone-700">Booking %</th>
              <th className="px-2 py-1.5 text-right font-semibold text-stone-700">Conversion %</th>
              <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Volume</th>
              <th className="px-2 py-1.5 text-left font-semibold text-stone-700">Strength</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.cohort} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-2 py-1.5 font-mono font-semibold text-stone-900">{r.cohort}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.acquired.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-blue-700">{r.booked.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-semibold">{r.sold.toLocaleString()}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{r.booked_pct}%</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-bold text-emerald-800">{r.conversion_pct}%</td>
                <td className="px-2 py-1.5"><div className="h-2 bg-stone-100 rounded"><div className="h-2 rounded" style={{ width: `${100*r.acquired/maxAcq}%`, background: '#0284C7' }} /></div></td>
                <td className="px-2 py-1.5"><div className="h-2 bg-stone-100 rounded"><div className="h-2 rounded" style={{ width: `${100*r.conversion_pct/maxConv}%`, background: `rgba(16,185,129,${0.3 + 0.7*r.conversion_pct/maxConv})` }} /></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* --------------------------- YoY TREND --------------------------- */
export function YoYTab({ params = {} }) {
  const [data, setData] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get('/reports/yoy-trend', { params }).then(r => setData(r.data)).catch(() => setData(null)); }, [pkey(params)]);

  const rows = data?.data || [];
  return (
    <div className="space-y-3" data-testid="reports-yoy-tab">
      <Card className="p-4 border-stone-200">
        <h3 className="font-heading text-lg font-bold text-stone-900">Year-over-Year — Monthly Trend</h3>
        <p className="text-xs text-stone-500 mt-0.5">
          {data ? `${data.previous_year} vs ${data.current_year}` : 'Loading…'} · leads & sold across 12 months
        </p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-3 border-stone-200">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Leads Received</div>
          <ResponsiveContainer width="100%" height={CHART_H}>
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="prev_leads" name={String(data?.previous_year || 'prev')} stroke="#A8A29E" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="cur_leads" name={String(data?.current_year || 'cur')} stroke="#0F766E" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-3 border-stone-200">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Units Sold</div>
          <ResponsiveContainer width="100%" height={CHART_H}>
            <LineChart data={rows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="prev_sold" name={String(data?.previous_year || 'prev')} stroke="#A8A29E" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="cur_sold" name={String(data?.current_year || 'cur')} stroke="#B45309" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

/* --------------------------- FUNNEL DETAILED --------------------------- */
export function FunnelDetailed({ params = {} }) {
  const [data, setData] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get('/reports/funnel-detailed', { params }).then(r => setData(r.data)).catch(() => setData(null)); }, [pkey(params)]);
  const stages = data?.stages || [];

  return (
    <Card className="p-4 border-stone-200" data-testid="reports-funnel-detailed">
      <h3 className="font-heading text-lg font-bold text-stone-900">Sales Funnel — Drop-off Analysis</h3>
      <p className="text-xs text-stone-500 mt-0.5">Every stage is inclusive (patient moves through). Drop-off shown vs prior stage.</p>
      <div className="mt-4 space-y-2">
        {stages.map((s, i) => {
          const colors = ['#0F766E', '#0284C7', '#B45309', '#DC2626'];
          return (
            <div key={s.stage} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-3 text-sm font-semibold text-stone-800">{s.stage}</div>
              <div className="col-span-6">
                <div className="h-8 bg-stone-100 rounded flex items-center overflow-hidden">
                  <div className="h-full flex items-center justify-end pr-2 text-white text-xs font-bold" style={{ width: `${s.pct_of_top}%`, background: colors[i % colors.length] }}>
                    {s.pct_of_top}%
                  </div>
                </div>
              </div>
              <div className="col-span-1 text-right text-sm tabular-nums font-bold text-stone-900">{s.count.toLocaleString()}</div>
              <div className="col-span-2 text-right text-xs">
                {i > 0 && <span className="text-red-600 font-semibold">↓ {s.drop_off_pct}%</span>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* --------------------------- SOURCE EFFECTIVENESS --------------------------- */
export function SourceEffectiveness({ params = {} }) {
  const [data, setData] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get('/reports/source-effectiveness', { params }).then(r => setData(r.data.data || [])).catch(() => setData([])); }, [pkey(params)]);

  return (
    <Card className="p-3 border-stone-200" data-testid="reports-source-effectiveness">
      <div className="mb-2">
        <h3 className="font-heading text-lg font-bold text-stone-900">Sub-source Effectiveness</h3>
        <p className="text-xs text-stone-500">Which sub-sources convert best. Red = &gt;30% loss rate.</p>
      </div>
      <div className="overflow-auto max-h-[600px] hc-scrollbar rounded border border-stone-200">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 sticky top-0">
            <tr>
              {['Sub-source','Total','Sold','Booked','Lost','Conv %','Loss %','Efficacy'].map(h => (
                <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-700 border-b border-stone-200">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map(r => (
              <tr key={r.source} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="px-2 py-1.5 font-medium text-stone-900">{r.source}</td>
                <td className="px-2 py-1.5 tabular-nums">{r.total.toLocaleString()}</td>
                <td className="px-2 py-1.5 tabular-nums text-emerald-700 font-semibold">{r.sold}</td>
                <td className="px-2 py-1.5 tabular-nums text-blue-700">{r.booked}</td>
                <td className="px-2 py-1.5 tabular-nums text-red-700">{r.lost}</td>
                <td className="px-2 py-1.5 tabular-nums font-bold" style={{ color: r.conversion > 20 ? '#065F46' : r.conversion > 10 ? '#B45309' : '#7F1D1D' }}>{r.conversion}%</td>
                <td className="px-2 py-1.5 tabular-nums" style={{ color: r.loss_rate > 30 ? '#DC2626' : '#57534E' }}>{r.loss_rate}%</td>
                <td className="px-2 py-1.5">
                  <div className="h-2 w-24 bg-stone-100 rounded">
                    <div className="h-2 rounded" style={{ width: `${Math.min(100, r.conversion*3)}%`, background: r.conversion > 20 ? '#059669' : r.conversion > 10 ? '#B45309' : '#DC2626' }} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* --------------------------- GEO HEAT --------------------------- */
export function GeoHeatTab({ params = {} }) {
  const [rows, setRows] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get('/reports/geographic-heat', { params }).then(r => setRows(r.data.data || [])).catch(() => setRows([])); }, [pkey(params)]);
  const maxTotal = Math.max(...rows.map(r => r.total), 1);
  return (
    <Card className="p-4 border-stone-200" data-testid="reports-geo-heat">
      <h3 className="font-heading text-lg font-bold text-stone-900">Clinic Heatmap</h3>
      <p className="text-xs text-stone-500 mb-3">Every clinic ranked by patient volume, sold count, and revenue.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {rows.map(r => {
          const intensity = 0.15 + 0.85 * (r.total / maxTotal);
          return (
            <div key={r.clinic} className="rounded-lg border border-stone-200 p-2.5 hover:shadow-md transition-shadow" style={{ background: `rgba(15,118,110,${intensity * 0.15})` }}>
              <div className="text-xs font-semibold text-stone-900 truncate">{r.clinic}</div>
              <div className="grid grid-cols-3 gap-1 mt-1.5 text-[10px]">
                <div><div className="text-stone-500">Total</div><div className="font-bold tabular-nums">{r.total.toLocaleString()}</div></div>
                <div><div className="text-stone-500">Sold</div><div className="font-bold tabular-nums text-emerald-700">{r.sold.toLocaleString()}</div></div>
                <div><div className="text-stone-500">Conv</div><div className="font-bold tabular-nums">{r.conversion}%</div></div>
              </div>
              {r.revenue > 0 && (
                <div className="mt-1 text-[10px] text-stone-600">₹ {(r.revenue/100000).toFixed(1)}L</div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
